import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { WA_NUMBERS } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, Save, Trash2, Phone, X, Star, Info } from "lucide-react";
import ExportButton from "@/components/ExportButton";
import SearchBar, { useLocalFilter } from "@/components/SearchBar";

export default function WhatsAppNumbers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState("");
  const filtered = useLocalFilter(items, q, ["label", "phone_number"]);
  const load = async () => setItems((await api.get("/whatsapp-numbers")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.label.trim() || !editing.phone_number.trim()) return toast.error("Label + phone required");
    if (!editing.phone_number.startsWith("+")) return toast.error("Phone must be in E.164 format (+countrycode…)");
    try {
      await api.post("/whatsapp-numbers", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };
  const remove = async (id) => {
    // Two-click confirm — native confirm() is blocked by the preview iframe.
    if (confirmingDelete !== id) {
      setConfirmingDelete(id);
      setTimeout(() => setConfirmingDelete((cur) => (cur === id ? "" : cur)), 5000);
      return;
    }
    setConfirmingDelete("");
    try {
      await api.delete(`/whatsapp-numbers/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={WA_NUMBERS.root} className="p-8 lg:p-12 max-w-[900px]">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Channels</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Phone className="h-7 w-7 text-orange-600" /> WhatsApp Numbers
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Manage sender numbers (used by PrepHacker to send to users) and admin recipient numbers (receive daily/weekly digests). In dev these are mock; on Contabo they&rsquo;ll route through Meta Cloud API.
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton path="/exports/whatsapp-numbers.xlsx" filename="whatsapp_numbers.xlsx" testId="wa-export-btn" />
          <button
            data-testid={WA_NUMBERS.addBtn}
            onClick={() => setEditing({ label: "", phone_number: "+91", is_admin_recipient: false, is_admin_assistant: false, is_sender: false, is_default_sender: false, active: true })}
            className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150"
          >
            <Plus className="h-3.5 w-3.5" /> Add number
          </button>
        </div>
      </div>

      <div className="mb-4"><SearchBar value={q} onChange={setQ} placeholder="Search numbers…" testId="wa-search" /></div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Label</th>
              <th className="text-left px-4 py-3 font-semibold">Number</th>
              <th className="text-left px-4 py-3 font-semibold">Role</th>
              <th className="text-left px-4 py-3 font-semibold">Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((n) => (
              <tr key={n.id} data-testid={`${WA_NUMBERS.itemPrefix}${n.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150">
                <td className="px-4 py-3 font-semibold">{n.label}</td>
                <td className="px-4 py-3 font-mono tabular">{n.phone_number}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {n.is_admin_recipient && <span className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded font-semibold uppercase tracking-widest">Admin digest</span>}
                    {n.is_admin_assistant && <span className="text-[10px] bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded font-semibold uppercase tracking-widest">Ask Pippy</span>}
                    {n.is_sender && <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded font-semibold uppercase tracking-widest">Sender</span>}
                    {n.is_default_sender && <span className="text-[10px] bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded font-semibold uppercase tracking-widest inline-flex items-center gap-1"><Star className="h-2.5 w-2.5" />Default</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`h-2 w-2 rounded-full inline-block ${n.active ? "bg-emerald-500" : "bg-slate-300"}`} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(n)} className="text-xs font-semibold text-slate-700 hover:text-orange-600 mr-3">Edit</button>
                  <button
                    onClick={() => remove(n.id)}
                    className={`text-xs font-semibold inline-flex items-center gap-1 transition-colors duration-150 ${confirmingDelete === n.id ? "text-white bg-red-600 px-2 py-0.5 rounded" : "text-slate-400 hover:text-red-600"}`}
                    title={confirmingDelete === n.id ? "Click again to confirm" : "Delete this number"}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {confirmingDelete === n.id && <span>Click to confirm</span>}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-500 text-sm">No numbers match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg tracking-tight">{editing.id ? "Edit number" : "New number"}</h3>
              <button onClick={() => setEditing(null)}><X className="h-4 w-4 text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Label (e.g. Ravi - Admin phone)" value={editing.label}
                onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none" />
              <input placeholder="+91XXXXXXXXXX" value={editing.phone_number}
                onChange={(e) => setEditing({ ...editing, phone_number: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono" />
              <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 flex gap-2 items-start">
                <Info className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                <div className="text-[11px] text-indigo-900 leading-relaxed">
                  A phone number can play multiple roles at once. Only <b>one</b> number in the whole app can be the
                  default sender at a time — the app auto-flips others off if you turn this one on.
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={editing.is_admin_recipient}
                  onChange={(e) => setEditing({ ...editing, is_admin_recipient: e.target.checked })} />
                <span>
                  <b>Receives admin digests</b>
                  <div className="text-[11px] text-slate-500 leading-snug">
                    This number gets the daily/weekly PrepHacker Connect summary via WhatsApp (top leads, conversion metrics, escalations).
                  </div>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={editing.is_admin_assistant || false}
                  onChange={(e) => setEditing({ ...editing, is_admin_assistant: e.target.checked })} />
                <span>
                  <b>Authorised for &ldquo;Ask Pippy&rdquo; via WhatsApp</b>
                  <div className="text-[11px] text-slate-500 leading-snug">
                    From this number, the admin can text the app's AI supervisor over WhatsApp
                    (e.g. &ldquo;how many hot leads today?&rdquo;) and get a reply. Grant only to trusted admins.
                  </div>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={editing.is_sender}
                  onChange={(e) => setEditing({ ...editing, is_sender: e.target.checked })} />
                <span>
                  <b>Sender number</b>
                  <div className="text-[11px] text-slate-500 leading-snug">
                    Allowed to send outbound messages to trial users (cadence + AI drafts). Tick this on business
                    numbers that are verified on Meta WhatsApp Cloud API. Personal numbers should be left off.
                  </div>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={editing.is_default_sender}
                  onChange={(e) => setEditing({ ...editing, is_default_sender: e.target.checked })} />
                <span>
                  <b>Default sender</b>
                  <div className="text-[11px] text-slate-500 leading-snug">
                    The single number used by the AI when no per-user rule assigns another. Turning this on
                    automatically turns it off on every other number.
                  </div>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                <span>
                  <b>Active</b>
                  <div className="text-[11px] text-slate-500 leading-snug">
                    Master switch for this number. When off, the app treats it as if it doesn't exist — no
                    sending, no digest receipt, no assistant access.
                  </div>
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button data-testid={WA_NUMBERS.saveBtn} onClick={save} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500 transition-colors duration-150">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
